/**
 * Bildirim tercihleri (Görünüm › Bildirimler; devam/gorunum/SOZLESME-B.md §1 `bildirim`).
 *
 * Tercih Redux'tan (`state.theme.gorunum.bildirim`) useBildirimTercihi ile buraya aktarılır; bildirimi gösteren
 * yardımcılar (notifier.tsx, MinoNotificationProvider, notificationHelper.showAntNotification) süre / konum / ses /
 * masaüstü kararını buradan alır. Varsayılanlar bugünkü davranıştır: üst ortada, 4,5 sn, hata da kapanır, ses ve masaüstü
 * bildirimi kapalı.
 */
import {notification} from "antd";
import {
    BILDIRIM_YERLESIMI,
    BildirimKonumu,
    BildirimTercihi,
    GORUNUM_SECENEKLERI,
    VARSAYILAN_BILDIRIM,
} from "../../gorunum/gorunumTercihi";

/* Tipler ve varsayılanlar TEK KAYNAKTAN (core/gorunum/gorunumTercihi.ts, SOZLESME-B §1): konum adları orada belirlenir. */
export type {BildirimKonumu, BildirimTercihi};
export {VARSAYILAN_BILDIRIM};
export type BildirimTuru = "error" | "warning" | "success";
export type BildirimYerlesimi = "top" | "topLeft" | "topRight" | "bottomLeft" | "bottomRight" | "bottom";

/** "ust" = bugünkü (üst orta); Görünüm ekranındaki seçenek sırası */
export const BILDIRIM_KONUMLARI: ReadonlyArray<BildirimKonumu> = GORUNUM_SECENEKLERI.bildirimKonumu;

/** bugünkü süre (sn) */
export const NORMAL_SURE = 4.5;
export const KISA_SURE = 2;
/** "Hata kapatılana dek kalsın" açıkken ekranda en çok bu kadar kalıcı hata (üst üste yığılmasın); fazlasında en eskisi kapanır */
export const MAKS_KALICI_HATA = 5;

export const KONUM_YERLESIMI: Readonly<Record<BildirimKonumu, BildirimYerlesimi>> = BILDIRIM_YERLESIMI;

/** başka adlarla yazılmış konumları da tanı (antd adları, ters sıralı Türkçe adlar) */
const KONUM_ESANLAMLI: Record<string, BildirimKonumu> = {
    ust: "ust", ustOrta: "ust", top: "ust",
    sagUst: "sagUst", ustSag: "sagUst", topRight: "sagUst",
    sagAlt: "sagAlt", altSag: "sagAlt", bottomRight: "sagAlt",
    solAlt: "solAlt", altSol: "solAlt", bottomLeft: "solAlt",
};

/** ham değerden (Redux / sunucu) geçerli tercih: eksik alan varsayılan, bilinmeyen değer atılır */
export const bildirimTercihiCoz = (ham: unknown): BildirimTercihi => {
    const k: any = ham && typeof ham === "object" && !Array.isArray(ham) ? ham : {};
    const v = VARSAYILAN_BILDIRIM;
    return {
        konum: (typeof k.konum === "string" && Object.prototype.hasOwnProperty.call(KONUM_ESANLAMLI, k.konum))
            ? KONUM_ESANLAMLI[k.konum] : v.konum,
        basariSuresi: k.basariSuresi === "kisa" ? "kisa" : "normal",
        hataKalici: typeof k.hataKalici === "boolean" ? k.hataKalici : v.hataKalici,
        ses: typeof k.ses === "boolean" ? k.ses : v.ses,
        masaustu: typeof k.masaustu === "boolean" ? k.masaustu : v.masaustu,
    };
};

let etkin: BildirimTercihi = VARSAYILAN_BILDIRIM;

export const bildirimTercihiAyarla = (t: BildirimTercihi): void => {
    etkin = t;
};

export const bildirimTercihiAl = (): BildirimTercihi => etkin;

/** gösterilecek süre (sn; 0 = kapatılana dek): çağıranın verdiği süre önce gelir; `normal`: tercihsiz süre */
export const bildirimSuresi = (tur: BildirimTuru, acikSure?: number, normal: number = NORMAL_SURE): number => {
    if (acikSure !== undefined) {
        return acikSure;
    }
    if (tur === "error" && etkin.hataKalici) {
        return 0;
    }
    if (tur === "success" && etkin.basariSuresi === "kisa") {
        return Math.min(KISA_SURE, normal);
    }
    return normal;
};

/** tercihteki konumun antd yerleşimi */
export const bildirimYerlesimi = (): BildirimYerlesimi => KONUM_YERLESIMI[etkin.konum] ?? "top";

/*
 * Kalıcı hata sınırı. antd'nin `maxCount`ı bildirim örneği ilk kez oluşurken sabitlenir ve HER bildirime uygulanır (bugün
 * sınır yok: varsayılan tercihte davranış değişirdi). Bunun yerine yalnız tercih yüzünden kalıcı olan hatalar sayılır;
 * yeni biri gelince en eskisi `notification.close` ile kapanır (statik örnek ve useNotification aynı örneği kullanır).
 * Kullanıcının kapattığı anahtar listede kalabilir: kapatması zararsızdır, ekrandaki kalıcı hata sayısı yine ≤ sınır.
 */
const kaliciHatalar: string[] = [];
let kaliciSayac = 0;

/**
 * Bildirimin anahtarı: tercih yüzünden kalıcı olan hataya (çağıran süre vermemiş, "hata kalıcı" açık) anahtar verir ve
 * sınırı uygular; öbür durumlarda çağıranın anahtarını olduğu gibi döndürür (bugünkü davranış).
 */
export const bildirimAnahtari = (tur: BildirimTuru, acikSure?: number, anahtar?: string): string | undefined => {
    if (acikSure !== undefined || tur !== "error" || !etkin.hataKalici) {
        return anahtar;
    }
    const k = anahtar ?? `mino-kalici-hata-${++kaliciSayac}`;
    const eski = kaliciHatalar.indexOf(k);
    if (eski >= 0) {
        kaliciHatalar.splice(eski, 1);
    }
    kaliciHatalar.push(k);
    while (kaliciHatalar.length > MAKS_KALICI_HATA) {
        const kapanacak = kaliciHatalar.shift();
        if (kapanacak) {
            try {
                notification.close(kapanacak);
            } catch (e) {
                /* örnek henüz yok: geç */
            }
        }
    }
    return k;
};

/* ---------- masaüstü bildirimi ---------- */

export type MasaustuIzni = "granted" | "denied" | "default" | "desteklenmiyor";

const destekleniyor = (): boolean => typeof window !== "undefined" && "Notification" in window;

/** tarayıcının masaüstü bildirim izni (Görünüm ekranında açıklama için) */
export const masaustuIzinDurumu = (): MasaustuIzni =>
    destekleniyor() ? (Notification.permission as MasaustuIzni) : "desteklenmiyor";

/**
 * Görünüm ekranında "Masaüstü bildirimi" anahtarı açılınca çağrılır (kullanıcı tıklaması içinde — tarayıcılar izni
 * yalnız kullanıcı hareketiyle sorar). İzin verildiyse true; reddedildi / desteklenmiyorsa false (anahtar kapalı kalmalı).
 */
export const masaustuIzniIste = async (): Promise<boolean> => {
    if (!destekleniyor()) {
        return false;
    }
    if (Notification.permission === "granted") {
        return true;
    }
    if (Notification.permission === "denied") {
        return false;
    }
    try {
        return (await Notification.requestPermission()) === "granted";
    } catch (e) {
        return false;
    }
};
